import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiErrorMessage, formatCurrency, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import {
    Printer, DownloadSimple, WhatsappLogo, CaretLeft,
} from "@phosphor-icons/react";
import { BRAND_LOGO_URL, BRAND_NAME } from "@/lib/brand";
import { buildWhatsAppUrl, invoiceShareMessage } from "@/lib/whatsapp";

const PAPER_SIZES = {
    a4:  { label: "A4",   className: "print-a4"  },
    "80": { label: "80mm", className: "print-80" },
    "58": { label: "58mm", className: "print-58" },
};

export default function Invoice() {
    const { saleId } = useParams();
    const navigate = useNavigate();
    const [sale, setSale] = useState(null);
    const [customer, setCustomer] = useState(null);
    const [payments, setPayments] = useState([]);
    const [paper, setPaper] = useState("a4");
    const [duplicate, setDuplicate] = useState(false);
    const [estimate, setEstimate] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const s = await api.get(`/sales/${saleId}`);
                setSale(s.data);
                if (s.data.customer_id) {
                    const c = await api.get(`/customers`);
                    setCustomer(c.data.find((x) => x.id === s.data.customer_id));
                }
                const p = await api.get(`/payments`, { params: { sale_id: saleId } });
                setPayments(p.data);
            } catch (e) { toast.error(apiErrorMessage(e)); }
        })();
    }, [saleId]);

    if (!sale) return <div className="p-8 text-sm text-neutral-500">Loading invoice...</div>;

    const paidAmount = payments.reduce((sum, p) => sum + Number(p.amount || 0), 0);
    const balance = Math.max(0, Number(sale.total || 0) - paidAmount);

    const shareWhatsApp = () => {
        if (!customer) return toast.error("Add a customer to share via WhatsApp");
        const msg = invoiceShareMessage({
            customerName: customer.name,
            invoiceNo: sale.order_no,
            total: sale.total,
            invoiceUrl: window.location.href,
        });
        window.open(buildWhatsAppUrl(customer.whatsapp || customer.mobile, msg), "_blank");
    };

    return (
        <div className="min-h-screen bg-neutral-100">
            {/* Toolbar */}
            <div className="no-print sticky top-0 z-20 bg-white border-b border-neutral-200 px-4 md:px-8 py-3 flex flex-wrap items-center gap-3">
                <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-neutral-600 hover:text-neutral-900" data-testid="invoice-back">
                    <CaretLeft size={16} /> Back
                </button>
                <div className="text-sm text-neutral-500 hidden md:block">Invoice · <span className="font-mono font-semibold text-neutral-900">{sale.order_no}</span></div>
                <div className="ml-auto flex flex-wrap items-center gap-2">
                    <div className="flex gap-1 border border-neutral-200 rounded p-0.5">
                        {Object.entries(PAPER_SIZES).map(([k, v]) => (
                            <button key={k} onClick={() => setPaper(k)}
                                    className={"px-3 py-1 text-xs font-semibold rounded " + (paper === k ? "bg-black text-white" : "text-neutral-600 hover:text-neutral-900")}
                                    data-testid={`invoice-paper-${k}`}>
                                {v.label}
                            </button>
                        ))}
                    </div>
                    <label className="text-xs font-medium text-neutral-600 flex items-center gap-1.5 px-2 cursor-pointer">
                        <input type="checkbox" checked={duplicate} onChange={(e) => setDuplicate(e.target.checked)} /> Duplicate
                    </label>
                    <label className="text-xs font-medium text-neutral-600 flex items-center gap-1.5 px-2 cursor-pointer">
                        <input type="checkbox" checked={estimate} onChange={(e) => setEstimate(e.target.checked)} /> Estimate
                    </label>
                    <Button variant="outline" onClick={shareWhatsApp} data-testid="invoice-whatsapp">
                        <WhatsappLogo size={14} weight="fill" className="mr-1.5" style={{ color: "#25D366" }} /> WhatsApp
                    </Button>
                    <Button onClick={() => window.print()} className="text-white" style={{ backgroundColor: "#15803D" }} data-testid="invoice-print">
                        <Printer size={14} className="mr-1.5" /> Print / PDF
                    </Button>
                </div>
            </div>

            {/* Paper */}
            <div className="print-area py-6 md:py-10 flex justify-center">
                <div className={"invoice-paper bg-white text-black shadow print-page " + PAPER_SIZES[paper].className}>
                    <InvoiceBody sale={sale} customer={customer} paidAmount={paidAmount} balance={balance}
                                 duplicate={duplicate} estimate={estimate} paper={paper} />
                </div>
            </div>
        </div>
    );
}

function InvoiceBody({ sale, customer, paidAmount, balance, duplicate, estimate, paper }) {
    const isThermal = paper === "58" || paper === "80";
    const docTitle = estimate ? "Estimate" : "Tax Invoice";
    return (
        <div className="p-5 md:p-8">
            {duplicate && !estimate && (
                <div className="text-center text-[10px] font-bold uppercase tracking-[0.3em] text-red-600 border border-red-300 py-1 mb-3">
                    Duplicate Copy
                </div>
            )}
            {/* Header */}
            <div className="flex items-start justify-between gap-4 pb-4 border-b-2" style={{ borderColor: "#0A0A0A" }}>
                <div className="flex items-start gap-3">
                    <div className="w-14 h-14 rounded overflow-hidden bg-white border border-neutral-200 flex items-center justify-center shrink-0">
                        <img src={BRAND_LOGO_URL} alt={BRAND_NAME} className="w-full h-full object-contain" />
                    </div>
                    <div>
                        <div className="text-lg font-extrabold font-display leading-tight">{BRAND_NAME}</div>
                        <div className="text-[10px] text-neutral-500 uppercase tracking-widest mt-0.5">Premium Fiber Craft · Fountains · Decor</div>
                        <div className="text-[11px] text-neutral-600 mt-1.5">bsfiberproduct@gmail.com</div>
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">{docTitle}</div>
                    <div className="text-lg font-extrabold font-mono">{sale.order_no}</div>
                    <div className="text-[11px] text-neutral-600 mt-1">{formatDate(sale.created_at)}</div>
                </div>
            </div>

            {/* Bill To + QR */}
            <div className="flex items-start justify-between gap-4 my-4">
                <div className="flex-1">
                    <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold mb-1">Bill To</div>
                    <div className="font-bold text-sm">{sale.customer_name}</div>
                    {customer?.mobile && <div className="text-xs text-neutral-700">📱 {customer.mobile}</div>}
                    {customer?.email && <div className="text-xs text-neutral-700">✉ {customer.email}</div>}
                    {customer?.gst_number && <div className="text-xs text-neutral-700 font-mono">GST: {customer.gst_number}</div>}
                    {customer?.address && <div className="text-xs text-neutral-600 mt-0.5 max-w-xs">{customer.address}</div>}
                </div>
                {!isThermal && (
                    <div className="text-center">
                        <QRCodeSVG value={sale.order_no} size={72} bgColor="#ffffff" fgColor="#0A0A0A" />
                        <div className="text-[9px] font-mono mt-1">{sale.order_no}</div>
                    </div>
                )}
            </div>

            {/* Items */}
            <table className="w-full text-xs border-t border-b border-neutral-300">
                <thead>
                    <tr className="text-left" style={{ background: "#F5F5F5" }}>
                        <th className="py-1.5 px-2 font-bold uppercase tracking-wider text-[9px]">#</th>
                        <th className="py-1.5 px-2 font-bold uppercase tracking-wider text-[9px]">Item</th>
                        <th className="py-1.5 px-2 text-right font-bold uppercase tracking-wider text-[9px]">Qty</th>
                        <th className="py-1.5 px-2 text-right font-bold uppercase tracking-wider text-[9px]">Rate</th>
                        {!isThermal && <th className="py-1.5 px-2 text-right font-bold uppercase tracking-wider text-[9px]">GST</th>}
                        <th className="py-1.5 px-2 text-right font-bold uppercase tracking-wider text-[9px]">Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {sale.items.map((it, i) => (
                        <tr key={i} className="border-t border-neutral-100">
                            <td className="py-1.5 px-2">{i + 1}</td>
                            <td className="py-1.5 px-2">
                                <div className="font-medium">{it.product_name}</div>
                                <div className="text-[9px] font-mono text-neutral-500">{it.sku}</div>
                            </td>
                            <td className="py-1.5 px-2 text-right tabular">{it.quantity}</td>
                            <td className="py-1.5 px-2 text-right tabular">{formatCurrency(it.unit_price)}</td>
                            {!isThermal && <td className="py-1.5 px-2 text-right tabular">{it.gst_percent}%</td>}
                            <td className="py-1.5 px-2 text-right tabular font-semibold">{formatCurrency(it.line_total)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Totals */}
            <div className="flex justify-end mt-3">
                <div className="w-full max-w-[280px] text-xs space-y-1">
                    <div className="flex justify-between"><span className="text-neutral-600">Subtotal</span><span className="tabular">{formatCurrency(sale.subtotal)}</span></div>
                    <div className="flex justify-between"><span className="text-neutral-600">GST</span><span className="tabular">{formatCurrency(sale.tax)}</span></div>
                    <div className="flex justify-between"><span className="text-neutral-600">Discount</span><span className="tabular">-{formatCurrency(sale.discount)}</span></div>
                    <div className="flex justify-between border-t-2 border-black pt-1.5 mt-1.5 text-sm font-extrabold font-display">
                        <span>Grand Total</span><span className="tabular" style={{ color: "#15803D" }}>{formatCurrency(sale.total)}</span>
                    </div>
                    {!estimate && (
                        <>
                            <div className="flex justify-between pt-1.5"><span className="text-neutral-600">Paid</span><span className="tabular">{formatCurrency(paidAmount)}</span></div>
                            <div className="flex justify-between font-bold" style={{ color: balance > 0 ? "#DC2626" : "#15803D" }}>
                                <span>Balance Due</span><span className="tabular">{formatCurrency(balance)}</span>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {!estimate && (
                <div className="mt-3 text-center">
                    <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded"
                          style={{ background: balance <= 0 ? "#DCFCE7" : "#FEF3C7", color: balance <= 0 ? "#15803D" : "#B45309" }}>
                        Payment Status: {balance <= 0 ? "PAID" : paidAmount > 0 ? "PARTIAL" : "UNPAID"}
                    </span>
                </div>
            )}

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-neutral-300 text-[10px] text-neutral-500 flex justify-between gap-3">
                <div className="max-w-[65%]">
                    <div className="font-bold text-neutral-700 mb-1">Terms & Notes</div>
                    <div>Goods once sold will not be taken back or exchanged.</div>
                    <div>Payment due within 30 days from invoice date.</div>
                    {sale.notes && <div className="mt-1 italic">{sale.notes}</div>}
                </div>
                <div className="text-right">
                    <div className="h-8"></div>
                    <div className="border-t border-neutral-400 pt-1 min-w-[110px]">
                        <div className="text-[9px] uppercase tracking-wider">Authorized Signature</div>
                        <div className="font-bold text-neutral-700 mt-0.5">for {BRAND_NAME}</div>
                    </div>
                </div>
            </div>
            <div className="text-center mt-4 text-[9px] text-neutral-400 uppercase tracking-widest">
                Thank you for choosing {BRAND_NAME} · Every fiber, hand-crafted.
            </div>
        </div>
    );
}
